import { useTranslation } from 'react-i18next';

import { JobStatus } from 'cliOld/enums';
import { cliJobService } from 'cliOld/services';
import { ICliJob } from 'cliOld/types';
import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';

export const useJobChecker = () => {
  const DEFAULT_INTERVAL_MILLISECONDS = 2000;

  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const checkJobStatus = (
    cliJob: ICliJob,
    finishedHandler: (cliJob: ICliJob) => void,
    stopppedHandler: (cliJob: ICliJob) => void,
    intervalMiliseconds = DEFAULT_INTERVAL_MILLISECONDS
  ) => {
    const abortController = new AbortController();

    const checkIntervalId = setInterval(() => {
      onCheckJobStatus(cliJob, checkIntervalId, finishedHandler, stopppedHandler, abortController);
    }, intervalMiliseconds);
  };

  const onCheckJobStatus = async (
    cliJob: ICliJob,
    checkIntervalId: NodeJS.Timer,
    finishedCallback: (cliJob: ICliJob) => void,
    stoppedCallback: (cliJob: ICliJob) => void,
    abortController?: AbortController
  ) => {
    try {
      const { record } = await cliJobService.getDetails(
        { job: cliJob.id },
        abortController?.signal || undefined
      );

      if (!record) {
        showNotification({
          message: t('errorInvalidCliJob'),
          type: ApiStatuses.ERROR,
          position: NotificationPosition.TOP,
        });

        return;
      }

      onCheckSuccess(record, checkIntervalId, finishedCallback, stoppedCallback, abortController);

      return;
    } catch (error) {
      // Do nothing
      // Prevents `Uncaught (in promise) > CanceledError` error
    }
  };

  const onCheckSuccess = async (
    cliJob: ICliJob,
    checkIntervalId: NodeJS.Timer,
    finishedCallback: (cliJob: ICliJob) => void,
    stoppedCallback: (cliJob: ICliJob) => void,
    abortController?: AbortController
  ) => {
    switch (cliJob.status) {
      case JobStatus.FINISHED:
        abortController.abort();
        clearInterval(checkIntervalId);

        finishedCallback(cliJob);

        break;
      case JobStatus.FAILED:
      case JobStatus.CANCELED:
        abortController.abort();
        clearInterval(checkIntervalId);

        stoppedCallback(cliJob);

        break;
      default:
    }
  };

  return {
    checkJobStatus,
  };
};
